// UserPreferencesContext.js - Complete file
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';

// Create the UserPreferencesContext
const UserPreferencesContext = createContext();

export const UserPreferencesProvider = ({ children }) => {
    const { authState } = useAuth();
    const { addError } = useError();
    const userId = authState?.user?.userId;

    // Store all user preferences in one object
    const [userPreferences, setUserPreferences] = useState(() => {
        try {
            // Try to load from localStorage if available
            const savedPreferences = localStorage.getItem('userPreferences');
            return savedPreferences ? JSON.parse(savedPreferences) : {};
        } catch (error) {
            console.error('[UserPreferencesContext] Error loading preferences from localStorage:', error);
            return {};
        }
    });

    // Debug log when userId changes
    useEffect(() => {
        console.log("[UserPreferencesContext] Current userId from authState:", userId);
    }, [userId]);

    // Save to localStorage whenever preferences change
    useEffect(() => {
        try {
            localStorage.setItem('userPreferences', JSON.stringify(userPreferences));

            // Also store the current user's follow-up email setting separately for easier access
            if (userId && userPreferences[userId]) {
                const isFollowUpEnabled = userPreferences[userId].defaultFollowUpEmail === true;
                localStorage.setItem(`followup_${userId}`, isFollowUpEnabled ? 'true' : 'false');
                console.log(`[UserPreferencesContext] Saved follow-up preference for ${userId}: ${isFollowUpEnabled}`);
            }
        } catch (error) {
            console.error('[UserPreferencesContext] Error saving preferences to localStorage:', error);
            addError('Failed to save preferences locally. Please check your browser storage settings.');
        }
    }, [userPreferences, userId, addError]);

    // Validate user ID
    const validateUserId = useCallback((id) => {
        if (!id || typeof id !== 'string' || id.trim() === '') {
            throw new Error('Invalid user ID provided');
        }
        return id.trim();
    }, []);

    // Validate boolean preference value
    const validateBooleanPreference = useCallback((value) => {
        if (typeof value !== 'boolean') {
            throw new Error('Preference value must be a boolean');
        }
        return value;
    }, []);

    // Set default follow-up email preference for a user
    const setDefaultFollowUpEmail = useCallback(async (isEnabled) => {
        try {
            // Validate inputs
            const validUserId = validateUserId(userId);
            const validIsEnabled = validateBooleanPreference(isEnabled);

            console.log(`[UserPreferencesContext] Setting default follow-up email for ${validUserId} to ${validIsEnabled}`);

            // Update local state
            setUserPreferences(prevPreferences => {
                const userPrefs = prevPreferences[validUserId] || {};
                return {
                    ...prevPreferences,
                    [validUserId]: {
                        ...userPrefs,
                        defaultFollowUpEmail: validIsEnabled
                    }
                };
            });

            // TODO: Sync with backend API when available
            // Example backend sync:
            // try {
            //     await apiClient.put(`/api/user-preferences/${validUserId}`, {
            //         defaultFollowUpEmail: validIsEnabled
            //     });
            //     addError('Failed to sync preference with server. Changes saved locally.');
            // }

            console.log('✅ Preference updated successfully');
        } catch (error) {
            console.error('[UserPreferencesContext] Error setting follow-up email preference:', error);
            addError(error.message || 'Failed to update preference');
        }
    }, [userId, validateUserId, validateBooleanPreference, addError]);

    // Get default follow-up email preference for the current user
    const getDefaultFollowUpEmail = useCallback(() => {
        try {
            if (!userId) {
                console.warn("[UserPreferencesContext] Cannot get preference: No user ID available");
                return false;
            }

            const validUserId = validateUserId(userId);
            const userPrefs = userPreferences[validUserId] || {};

            const result = userPrefs.defaultFollowUpEmail === true;
            console.log(`[UserPreferencesContext] Getting follow-up preference for ${validUserId}: ${result}`);
            return result;
        } catch (error) {
            console.error('[UserPreferencesContext] Error getting follow-up email preference:', error);
            addError('Failed to retrieve preference');
            return false;
        }
    }, [userId, userPreferences, validateUserId, addError]);

    // Load user preferences from backend (for future use)
    const loadUserPreferences = useCallback(async () => {
        try {
            if (!userId) {
                console.warn("[UserPreferencesContext] Cannot load preferences: No user ID available");
                return;
            }

            const validUserId = validateUserId(userId);

            // TODO: Implement backend API call when available
            // Example:
            // const response = await apiClient.get(`/api/user-preferences/${validUserId}`);
            // const serverPreferences = response.data;
            // 
            // setUserPreferences(prevPreferences => ({
            //     ...prevPreferences,
            //     [validUserId]: {
            //         ...prevPreferences[validUserId],
            //         ...serverPreferences
            //     }
            // }));

            console.log(`[UserPreferencesContext] Loaded preferences for user ${validUserId}`);
        } catch (error) {
            console.error('[UserPreferencesContext] Error loading preferences from server:', error);
            addError('Failed to load preferences from server. Using local preferences.');
        }
    }, [userId, validateUserId, addError]);

    // Clear user preferences (for logout)
    const clearUserPreferences = useCallback(() => {
        try {
            if (!userId) {
                console.warn("[UserPreferencesContext] Cannot clear preferences: No user ID available");
                return;
            }

            const validUserId = validateUserId(userId);

            setUserPreferences(prevPreferences => {
                const newPreferences = { ...prevPreferences };
                delete newPreferences[validUserId];
                return newPreferences;
            });

            // Clear localStorage entry for this user
            localStorage.removeItem(`followup_${validUserId}`);

            console.log(`[UserPreferencesContext] Cleared preferences for user ${validUserId}`);
        } catch (error) {
            console.error('[UserPreferencesContext] Error clearing user preferences:', error);
            addError('Failed to clear preferences');
        }
    }, [userId, validateUserId, addError]);

    // Load preferences when user changes
    useEffect(() => {
        if (userId) {
            loadUserPreferences();
        }
    }, [userId, loadUserPreferences]);

    // Clear preferences when user logs out
    useEffect(() => {
        if (!userId && authState.isAuthenticated === false) {
            clearUserPreferences();
        }
    }, [userId, authState.isAuthenticated, clearUserPreferences]);

    return (
        <UserPreferencesContext.Provider
            value={{
                userPreferences,
                setDefaultFollowUpEmail,
                getDefaultFollowUpEmail,
                loadUserPreferences,
                clearUserPreferences,
                defaultFollowUpEmail: userId ? (userPreferences[userId]?.defaultFollowUpEmail === true) : false,
                isLoading: false, // TODO: Add loading state when implementing backend calls
                error: null // TODO: Add error state when implementing backend calls
            }}
        >
            {children}
        </UserPreferencesContext.Provider>
    );
};

// Custom hook to use the UserPreferencesContext
export const useUserPreferences = () => {
    const context = useContext(UserPreferencesContext);
    if (!context) {
        throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
    }
    return context;
};